import { useState, useEffect } from "react";
import "./App.css";
import NewBill from "./NewBill";
import { UserProvider, useUser } from "./UserContext";
import nangiaMark from "./assets/nangia-mark.png";
import nangiaFullLogo from "./assets/nangia-full-logo.png";

const navItems = [
  { icon: "▦", label: "Dashboard", id: "dashboard" },
  { icon: "▤", label: "People Bills", id: "people-bill" },
];

const PAGE_META = {
  dashboard: {
    eyebrow: "WORKSPACE / OVERVIEW",
    title: "Dashboard",
    sub: "High-level overview of your vendor bills, financial metrics, and operational compliance.",
  },
  "people-bill": {
    eyebrow: "WORKSPACE / MANAGEMENT",
    title: "People Bills",
    sub: "View and manage your digital bills and vendor records. Review transaction history and update bill details securely.",
  },
};

const COLUMNS = [
  { key: "Name", label: "Bill Name", width: 150 },
  { key: "Owner", label: "Owner", width: 100 },
  { key: "Article_Vendor", label: "Vendor", width: 100 },
  { key: "Amount", label: "Amount", width: 100 },
  { key: "Bill_Amount", label: "Bill Salary", width: 90 },
  { key: "Commission", label: "Commission", width: 90 },
  { key: "Bill_GST", label: "Bill GST", width: 95 },
  { key: "Location", label: "Location", width: 85 },
  { key: "Month", label: "Month", width: 75 },
  { key: "Attachment", label: "Attachment", width: 95 },
  { key: "Modified_Time", label: "Modified", width: 115 },
];

const PAGE_SIZE = 10;

const formatINR = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const formatINRDecimal = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const billCode = (id) => (id ? `PB-${String(id).slice(-6)}` : "—");

const getCellText = (row, colKey) => {
  const value = row[colKey];

  if (colKey === "Owner" || colKey === "Article_Vendor") return value?.name || "";

  if (
    colKey === "Amount" ||
    colKey === "Bill_Amount" ||
    colKey === "Commission" ||
    colKey === "Bill_GST"
  ) {
    return value !== undefined && value !== null && value !== "" ? Number(value).toFixed(2) : "";
  }

  if (colKey === "Modified_Time") return formatDate(value);

  if (colKey === "Attachment") {
    return !value || (Array.isArray(value) && value.length === 0) ? "" : "Attached";
  }

  return value ?? "";
};

const escapeCsvValue = (value) => {
  const str = String(value ?? "");
  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const rowsToCsv = (rows) => {
  const header = COLUMNS.map((col) => escapeCsvValue(col.label)).join(",");
  const body = rows
    .map((row) => COLUMNS.map((col) => escapeCsvValue(getCellText(row, col.key))).join(","))
    .join("\r\n");
  return `${header}\r\n${body}`;
};

const downloadCsv = (rows, filename) => {
  const csv = "﻿" + rowsToCsv(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

function StatusScreen({ title, subtitle, tone = "loading", action }) {
  return (
    <div className="loading-screen">
      <div className="loading-card">
        <div className="loading-brand">
          <img className="loading-brand-icon" src={nangiaMark} alt="Nangia" />
          <span className="loading-brand-name">Nangia</span>
        </div>
        {tone === "loading" ? (
          <div className="loading-spinner" />
        ) : (
          <div className="loading-icon loading-icon--denied">!</div>
        )}
        <div>
          <p className="loading-title">{title}</p>
          {subtitle && <p className="loading-sub">{subtitle}</p>}
        </div>
        {action}
      </div>
    </div>
  );
}

function AppContent() {
  const { user, loading: authLoading, accessDenied, deniedMessage, logout } = useUser();
  const [peopleData, setPeopleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeNav, setActiveNav] = useState("dashboard");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("Modified_Time");
  const [sortDir, setSortDir] = useState("desc");
  const [view, setView] = useState("list"); // 'list' or 'new-bill'
  const [editData, setEditData] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterLocation, setFilterLocation] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [page, setPage] = useState(1);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Using local server path for the backend function
      const response = await fetch('/server/dealer_backend/get-people-bills');
      const result = await response.json();
      if (result.status === "success") {
        setPeopleData(result.data || []);
      }
    } catch (error) {
      console.error("Error fetching people bills:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  useEffect(() => {
    setPage(1);
  }, [search, filterLocation, filterMonth]);

  if (authLoading) {
    return (
      <StatusScreen
        title="Verifying your session…"
        subtitle="Hang tight while we confirm you're signed in."
      />
    );
  }

  if (accessDenied) {
    return (
      <StatusScreen
        tone="denied"
        title="Access Denied"
        subtitle={deniedMessage}
        action={
          <button className="btn-secondary" onClick={logout}>
            Sign out
          </button>
        }
      />
    );
  }

  if (!user) {
    return (
      <StatusScreen
        title="Redirecting to login…"
        subtitle="Taking you to the sign-in page."
      />
    );
  }

  const locationOptions = Array.from(
    new Set(peopleData.map((p) => p.Location).filter(Boolean))
  );
  const monthOptions = Array.from(
    new Set(peopleData.map((p) => p.Month).filter(Boolean))
  );

  const filtered = peopleData
    .filter(
      (p) =>
        (p.Name || "").toLowerCase().includes(search.toLowerCase()) ||
        (p.Article_Vendor?.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (p.Location || "").toLowerCase().includes(search.toLowerCase())
    )
    .filter((p) => !filterLocation || p.Location === filterLocation)
    .filter((p) => !filterMonth || p.Month === filterMonth)
    .sort((a, b) => {
      let av = a[sortKey],
        bv = b[sortKey];

      // Handle nested objects like Owner or Article_Vendor if sorting by them
      if (av && typeof av === 'object') av = av.name || "";
      if (bv && typeof bv === 'object') bv = bv.name || "";

      if (typeof av === "string") {
        av = (av || "").toLowerCase();
        bv = (bv || "").toLowerCase();
      }
      return sortDir === "asc" ? (av > bv ? 1 : -1) : av < bv ? 1 : -1;
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(currentPage * PAGE_SIZE, filtered.length);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleDownload = () => {
    if (filtered.length === 0) return;
    const dateStr = new Date().toISOString().slice(0, 10);
    downloadCsv(filtered, `people-bills-${dateStr}.csv`);
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleEdit = (row) => {
    setEditData(row);
    setView("new-bill");
  };

  const renderCell = (row, colKey) => {
    const value = row[colKey];

    if (colKey === "Owner" || colKey === "Article_Vendor") {
      return value?.name || "—";
    }

    if (
      colKey === "Amount" ||
      colKey === "Bill_Amount" ||
      colKey === "Commission" ||
      colKey === "Bill_GST"
    ) {
      return value !== undefined && value !== null && value !== "" ? formatINRDecimal(value) : "—";
    }

    if (colKey === "Modified_Time") {
      return formatDate(value);
    }

    if (colKey === "Attachment") {
      if (!value || (Array.isArray(value) && value.length === 0)) return "—";
      return "📎 Attached";
    }

    if (colKey === "Name") {
      return (
        <div className="bill-name-cell">
          <span className="bill-icon">$</span>
          <div className="bill-name-text">
            <span className="bill-name-primary">{value}</span>
            <span className="bill-name-code">{billCode(row.id)}</span>
          </div>
        </div>
      );
    }

    return value || "—";
  };

  // ── Dashboard derived stats ──
  const totalAuthorizedSpend = peopleData.reduce(
    (sum, p) => sum + Number(p.Amount || 0),
    0
  );
  const totalBillsProcessed = peopleData.length;
  const missingAttachments = peopleData.filter(
    (p) => !p.Attachment || (Array.isArray(p.Attachment) && p.Attachment.length === 0)
  ).length;
  const spendByLocation = peopleData.reduce((acc, p) => {
    const loc = p.Location || "Unknown";
    acc[loc] = (acc[loc] || 0) + Number(p.Amount || 0);
    return acc;
  }, {});
  const topLocationEntry = Object.entries(spendByLocation).sort((a, b) => b[1] - a[1])[0];
  const spendByVendor = peopleData.reduce((acc, p) => {
    const name = p.Article_Vendor?.name || "Unknown Vendor";
    acc[name] = (acc[name] || 0) + Number(p.Amount || 0);
    return acc;
  }, {});
  const topVendors = Object.entries(spendByVendor).sort((a, b) => b[1] - a[1]).slice(0, 5);

  const meta = PAGE_META[activeNav] || PAGE_META.dashboard;
  const initial = (user.Name || "U").trim().charAt(0).toUpperCase();

  return (
    <div className="app">
      {/* ── Sidebar ── */}
      <aside className="sidebar">
        <div className="sidebar-top">
          <div className="brand">
            <img className="brand-icon" src={nangiaFullLogo} alt="Nangia" />
            <div className="brand-text">
              <span className="brand-name">Nangia</span>
              <span className="brand-sub">VENDOR PORTAL</span>
            </div>
          </div>
          <p className="nav-section-label">Main Menu</p>
          <nav className="nav">
            {navItems.map((item) => (
              <button
                key={item.id}
                className={`nav-item ${
                  activeNav === item.id ? "nav-item--active" : ""
                }`}
                onClick={() => {
                  setActiveNav(item.id);
                  setView("list");
                  setEditData(null);
                }}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="sidebar-bottom">
          <button className="nav-item nav-item--logout" onClick={logout}>
            <span className="nav-icon">⎋</span>
            <span className="nav-label">Logout</span>
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="main">
        <header className="topbar">
          <div className="breadcrumb">
            <span className="breadcrumb-link">Home</span>
            <span className="breadcrumb-sep">›</span>
            <span className="breadcrumb-current">{meta.title}</span>
          </div>
          <div className="profile-trigger">
            <span className="profile-avatar-sm">{initial}</span>
            <div className="profile-trigger-info">
              <p className="profile-trigger-name">{user.Name}</p>
              <p className="profile-trigger-role">Authorized Vendor</p>
            </div>
            <span className="profile-trigger-chevron">⌄</span>
          </div>
        </header>

        {!(activeNav === "people-bill" && view === "new-bill") && (
          <div className="page-hero">
            <p className="page-eyebrow">{meta.eyebrow}</p>
            <h1 className="page-hero-title">{meta.title}</h1>
            <p className="page-hero-sub">{meta.sub}</p>
          </div>
        )}

        {activeNav === "dashboard" ? (
          <div className="page-body">
            <div className="stat-grid">
              <div className="stat-card">
                <span className="stat-icon stat-icon--rupee">₹</span>
                <p className="stat-label">Total Authorized Spend</p>
                <p className="stat-value">{formatINR(totalAuthorizedSpend)}</p>
              </div>
              <div className="stat-card">
                <span className="stat-icon stat-icon--doc">▤</span>
                <p className="stat-label">Total Bills Processed</p>
                <p className="stat-value">{totalBillsProcessed}</p>
              </div>
              <div className="stat-card">
                <span className="stat-icon stat-icon--warn">!</span>
                <p className="stat-label">Missing Attachments</p>
                <p className="stat-value stat-value--danger">{missingAttachments}</p>
              </div>
              <div className="stat-card">
                <span className="stat-icon stat-icon--pin">◎</span>
                <p className="stat-label">Top Spend Location</p>
                <p className="stat-value stat-value--sm">
                  {topLocationEntry ? topLocationEntry[0] : "-"}
                </p>
                <p className="stat-footnote">
                  {formatINR(topLocationEntry ? topLocationEntry[1] : 0)}
                </p>
              </div>
            </div>

            <div className="content-card">
              <h3 className="content-card-title">Top Vendors by Spend</h3>
              {topVendors.length === 0 ? (
                <p className="td-empty">No vendor data available.</p>
              ) : (
                <ul className="vendor-rank-list">
                  {topVendors.map(([name, amount], idx) => (
                    <li key={name} className="vendor-rank-row">
                      <span className="vendor-rank-index">{idx + 1}</span>
                      <span className="vendor-rank-name">{name}</span>
                      <span className="vendor-rank-amount">{formatINR(amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : view === "list" ? (
          <div className="page-body">
            <div className="content-card content-card--table">
              <div className="card-header-row">
                <div>
                  <h3 className="content-card-title">Bill Directory</h3>
                  <p className="content-card-sub">
                    {loading ? "Loading…" : `Showing ${filtered.length} active records`}
                  </p>
                </div>
                <div className="header-actions">
                  <div className="search-wrap">
                    <span className="search-icon">⌕</span>
                    <input
                      className="search-input"
                      placeholder="Search bills…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <button
                    className="icon-btn"
                    title="Download CSV"
                    onClick={handleDownload}
                    disabled={filtered.length === 0}
                  >
                    ⭳
                  </button>
                  <div className="filter-wrap">
                    <button
                      className="icon-btn"
                      title="Filter"
                      onClick={() => setFilterOpen((v) => !v)}
                    >
                      ≡
                    </button>
                    {filterOpen && (
                      <div className="filter-panel">
                        <div className="filter-panel-head">
                          <span>Filter Records</span>
                          <button
                            className="filter-panel-close"
                            onClick={() => setFilterOpen(false)}
                          >
                            ✕
                          </button>
                        </div>
                        <div className="filter-group">
                          <label className="filter-label">Location</label>
                          <select
                            className="filter-select"
                            value={filterLocation}
                            onChange={(e) => setFilterLocation(e.target.value)}
                          >
                            <option value="">All Locations</option>
                            {locationOptions.map((loc) => (
                              <option key={loc} value={loc}>{loc}</option>
                            ))}
                          </select>
                        </div>
                        <div className="filter-group">
                          <label className="filter-label">Month</label>
                          <select
                            className="filter-select"
                            value={filterMonth}
                            onChange={(e) => setFilterMonth(e.target.value)}
                          >
                            <option value="">All Months</option>
                            {monthOptions.map((month) => (
                              <option key={month} value={month}>{month}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="table-wrapper">
                <table className="bill-table">
                  <thead>
                    <tr>
                      {COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          className={`th ${sortKey === col.key ? "th--sorted" : ""}`}
                          style={{ width: col.width, maxWidth: col.width }}
                          onClick={() => handleSort(col.key)}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={COLUMNS.length} className="td-empty">
                          Fetching records for {user.Vendor_Name}...
                        </td>
                      </tr>
                    ) : paginated.length === 0 ? (
                      <tr>
                        <td colSpan={COLUMNS.length} className="td-empty">
                          No bills found matching your search.
                        </td>
                      </tr>
                    ) : (
                      paginated.map((row) => (
                        <tr key={row.id} className="tr tr--clickable" onClick={() => handleEdit(row)}>
                          {COLUMNS.map((col) => (
                            <td
                              key={col.key}
                              className="td"
                              style={{ width: col.width, maxWidth: col.width }}
                            >
                              {renderCell(row, col.key)}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {!loading && (
                <div className="table-footer">
                  <span className="footer-count">
                    Showing <strong>{pageStart} - {pageEnd}</strong> of{" "}
                    <strong>{filtered.length}</strong> entries
                  </span>
                  <div className="pagination">
                    <button
                      className="page-btn"
                      disabled={currentPage === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      ‹
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                      <button
                        key={n}
                        className={`page-btn ${n === currentPage ? "page-btn--active" : ""}`}
                        onClick={() => setPage(n)}
                      >
                        {n}
                      </button>
                    ))}
                    <button
                      className="page-btn"
                      disabled={currentPage === totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      ›
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="page-body">
            <NewBill
              editData={editData}
              vendorInfo={user}
              onCancel={() => {
                setView("list");
                setEditData(null);
              }}
              onSuccess={() => {
                setView("list");
                setEditData(null);
                fetchData();
              }}
            />
          </div>
        )}
      </main>
    </div>
  );
}

export default function App() {
  return (
    <UserProvider>
      <AppContent />
    </UserProvider>
  );
}
